import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { renderToStaticMarkup } from 'react-dom/server'
import styled from 'styled-components'
import Heatmap from 'components/visualization/Heatmap'
import { CountType, DayHourTimeZone } from 'enums'
import { useDisplayTheme } from 'hooks/useUiState'
import { useResizeObserver } from 'hooks/useResizeObserver'
import { debounce } from 'lodash'
import messageUtil from 'utils/messageUtil'
import { rounding } from 'utils/widgetUtil'

import { DayAndHourDataType, DayAndHourWidgetsType, InsightsSelectFunc } from '../../types'
import { getWeekDayToNumber, weekDayAbbreviation, getDisplayTimeZone } from '../utils'
import Tooltip from './Tooltip'
import { getTooltipThemeByMode, getRedLabelColorByMode } from './themes'

const Container = styled.div`
  width: 100%;
  height: 100%;
`

interface HeatmapDisplayProps {
  dayAndHourData: DayAndHourDataType
  widgetSetting: DayAndHourWidgetsType
  onInsightsSelect?: InsightsSelectFunc
}

const HeatmapDisplay = ({
  dayAndHourData,
  widgetSetting,
  onInsightsSelect
}: HeatmapDisplayProps) => {
  const theme = useDisplayTheme()
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const [xAxisStep, setXAxisStep] = useState(3)
  const { countType, viewOption, timeZoneOption } = widgetSetting
  const { dataSet, extraInfo } = dayAndHourData

  const xCategories = (dataSet ?? []).slice(0, 24).map((item) => item.label.split(' ')[1])
  const yCategories = Object.values(weekDayAbbreviation).reverse()
  const heatmapData = (dataSet ?? []).map((item) => {
    const hourNumber = Number(item.name!.split('.')[1])
    const weekDay = item.label.split(' ')[0]
    return {
      x: hourNumber,
      y: getWeekDayToNumber(weekDay),
      value: item.share!
    }
  })

  const adjustXAxisStep = (observeEntry: ResizeObserverEntry[]) => {
    const containerWidth = observeEntry[0].contentRect.width
    setXAxisStep(containerWidth >= 1200 ? 1 : 3)
  }

  const getPointInfoByXY = (x: number, y: number) => dataSet![(6 - y) * 24 + x]

  useResizeObserver(containerRef, debounce(adjustXAxisStep, 10))

  return (
    <Container ref={containerRef}>
      <Heatmap
        xCategories={xCategories}
        yCategories={yCategories}
        chartTitle={`${messageUtil.viewOption(viewOption!)} ${
          timeZoneOption === DayHourTimeZone.GMT
            ? getDisplayTimeZone(extraInfo!)
            : `(${t('authorsLocalTimeZone')})`
        }`}
        showDataLabel={false}
        data={heatmapData}
        colorSetting={countType === CountType.POSTS ? 'gradient.post' : 'gradient.authors'}
        onSelectChange={(val: any) => {
          const pointInfos = val.map((p: any) => {
            const { x, y } = p
            const pointInfo = getPointInfoByXY(x, y)
            return pointInfo
          })
          onInsightsSelect?.(pointInfos)
        }}
        opposite={false}
        cellType="square"
        xAxisStep={xAxisStep}
        yAxisColorMap={{
          0: getRedLabelColorByMode(theme),
          1: getRedLabelColorByMode(theme)
        }}
        allowPointSelect
        dataUnitName={messageUtil.countType(countType!)}
        valueFormatter={(point) => `${rounding(point.value, 2)}%`}
        tooltipFormatter={(props) => {
          const { x, y } = props
          const pointInfo = getPointInfoByXY(x, y)
          return renderToStaticMarkup(
            <Tooltip
              pointInfo={pointInfo}
              countType={countType!}
              tooltipTheme={getTooltipThemeByMode(theme)}
            />
          )
        }}
      />
    </Container>
  )
}

export default HeatmapDisplay
